import React, { useState, useEffect } from 'react';
import { fetchGoogleSheetData } from '../lib/googleSheetsService';
import { logger } from '../lib/loggingService';
import { toStableCapturifyUrl } from '../lib/capturifyImages';

interface ImageItem {
  id: string;
  url: string;
  alt?: string;
}

interface DataScraperProps {
  onDataExtracted: (data: { 
    clientName: string; 
    clientAddress: string; 
    chimneyType: string; 
    reportDate: string; 
    timelineCoverImage: string;
    scrapedImages: ImageItem[];
    dataSourceUrl?: string;
  }) => void;
  setCurrentStep: (step: 'scrape' | 'form') => void;
  setFormData: (data: { 
    clientName: string; 
    clientAddress: string; 
    chimneyType: string; 
    reportDate: string; 
    timelineCoverImage: string;
    scrapedImages: ImageItem[];
  }) => void;
  userEmail?: string;
}

interface JobData {
  id: string;
  date: string;
  clientName: string;
  clientAddress: string;
  chimneyType: string;
  timelineContent: string;
  imageUrls: string[];
  timelineCoverImage: string;
  scrapedImages: ImageItem[];
}

interface CapturifyTimeline {
  clientName?: string;
  clientAddress?: string;
  coverImageUrl?: string;
  images?: Array<{
    id: number;
    inspectionDate?: string;
    createdAt?: string;
    rotation?: number;
    imageUrl: string;
  }>;
  count?: number;
}

interface ExtractedData {
  clientName: string;
  clientAddress: string;
  chimneyType: string;
  reportDate: string;
  timelineContent: string;
  imageUrls: string[];
  timelineCoverImage: string;
  scrapedImages: ImageItem[];
}

const Icon = {
  Link: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
  ),
  Zap: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" /></svg>
  ),
  Calendar: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
  ),
  Edit: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></svg>
  ),
  Folder: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2z" /></svg>
  ),
  AlertCircle: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><circle cx="12" cy="12" r="10" /><path d="M12 8v5M12 16h.01" /></svg>
  ),
  Shield: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
  ),
};

const DataScraper: React.FC<DataScraperProps> = ({ onDataExtracted, setCurrentStep, setFormData, userEmail = 'unknown' }) => {
  const API_BASE = process.env.REACT_APP_API_BASE || 'https://adminbackend.chimneysweeps.com';
  const API_KEY = process.env.REACT_APP_API_KEY || 'pit-0988e019-bccb-4b77-a0a6-f50e56c58522';
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData>({
    clientName: '',
    clientAddress: '',
    chimneyType: '',
    reportDate: new Date().toISOString().split('T')[0],
    timelineContent: '',
    imageUrls: [],
    timelineCoverImage: '',
    scrapedImages: []
  });
  const [showEditForm, setShowEditForm] = useState(false);
  const [showImageSelector, setShowImageSelector] = useState(false);
  const [showNoImagePopup, setShowNoImagePopup] = useState(false);
  const [showPreviousJobImages, setShowPreviousJobImages] = useState(false);
  const [showDateSelector, setShowDateSelector] = useState(false);
  const [allJobs, setAllJobs] = useState<JobData[]>([]);
  const [selectedDateJob, setSelectedDateJob] = useState<JobData | null>(null);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [libraryRootDirs, setLibraryRootDirs] = useState<Array<{ name: string; prefix: string }>>([]);
  const [libraryChildDirs, setLibraryChildDirs] = useState<Array<{ name: string; prefix: string }>>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sheetDataLoaded, setSheetDataLoaded] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sheetDataError, setSheetDataError] = useState<string | null>(null);

  // Load Google Sheets data on component mount (silent unless there's an error)
  useEffect(() => {
    const loadGoogleSheetsData = async () => {
      const sheetId = process.env.REACT_APP_GOOGLE_SHEET_ID || '1Bhz4JMVaR4tGbBKrhRHwR38MTtX8MVM_0v0JV8V6R9Q';
      const sheetRange = process.env.REACT_APP_GOOGLE_SHEET_RANGE || 'Sheet1!A:E';
      const apiKey = process.env.REACT_APP_GOOGLE_SHEETS_API_KEY || 'AIzaSyBixMaBdYAqO8_I9qlBlwU6nQkjiDCt-uc';
      
      if (!sheetId || !apiKey) {
        return;
      }

      try {
        const data = await fetchGoogleSheetData(sheetId, sheetRange);
        if (data.length > 0) {
          setSheetDataLoaded(true);
        }
      } catch (error: any) {
        console.error('✗ Failed to preload Google Sheets data:', error?.message || error);
      }
    };

    loadGoogleSheetsData();
  }, []);

  const openLibrary = async () => {
    setShowLibrary(true);
    setLibraryError(null);
    setLibraryChildDirs([]);
    setLibraryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/directories`, {
        headers: { 'X-API-Key': API_KEY }
      });
      if (!res.ok) throw new Error(`Failed to load directories: ${res.status}`);
      const data = await res.json();
      setLibraryRootDirs(Array.isArray(data.directories) ? data.directories : []);
    } catch (e: any) {
      setLibraryError(e?.message || 'Unable to load directories');
    } finally {
      setLibraryLoading(false);
    }
  };

  const loadChildDirectories = async (prefix: string) => {
    setLibraryError(null);
    setLibraryLoading(true);
    try {
      const url = `${API_BASE}/directories?prefix=${encodeURIComponent(prefix)}`;
      const res = await fetch(url, {
        headers: { 'X-API-Key': API_KEY }
      });
      if (!res.ok) throw new Error(`Failed to load subdirectories: ${res.status}`);
      const data = await res.json();
      setLibraryChildDirs(Array.isArray(data.directories) ? data.directories : []);
    } catch (e: any) {
      setLibraryError(e?.message || 'Unable to load subdirectories');
    } finally {
      setLibraryLoading(false);
    }
  };

  // Accepts a full Capturify timeline link
  // (https://capturify.chimneysweeps.com/timeline/<token>) or a bare token.
  const extractCapturifyToken = (input: string): string | null => {
    const trimmed = input.trim();
    const match = trimmed.match(/\/timeline\/([A-Za-z0-9_-]+)/);
    if (match) return match[1];
    return /^[A-Za-z0-9_-]+$/.test(trimmed) ? trimmed : null;
  };

  // Maps the Capturify public timeline JSON into the same shapes the CompanyCam
  // HTML parser used to produce: one job per inspection date (newest first),
  // with the newest date treated as the current job.
  const parseCapturifyTimeline = (timeline: CapturifyTimeline): { current: ExtractedData; jobs: JobData[] } => {
    const imagesByDate = new Map<string, string[]>();
    (timeline.images || []).forEach(image => {
      if (!image.imageUrl) return;
      const date = image.inspectionDate || (image.createdAt || '').split('T')[0] || new Date().toISOString().split('T')[0];
      if (!imagesByDate.has(date)) imagesByDate.set(date, []);
      imagesByDate.get(date)!.push(toStableCapturifyUrl(image.imageUrl));
    });

    const jobs: JobData[] = Array.from(imagesByDate.entries())
      .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
      .map(([date, imageUrls], index) => ({
        id: `job-${index}-${Date.now()}`,
        date,
        clientName: timeline.clientName || '',
        clientAddress: timeline.clientAddress || '',
        chimneyType: 'masonry',
        timelineContent: '',
        imageUrls,
        timelineCoverImage: imageUrls[0] || '',
        scrapedImages: imageUrls.map((url, imgIndex) => ({
          id: `job-${index}-img-${imgIndex}-${Date.now()}`,
          url,
          alt: `Job ${index + 1} image ${imgIndex + 1}`
        }))
      }));

    const timelineCoverImage = timeline.coverImageUrl ? toStableCapturifyUrl(timeline.coverImageUrl) : '';
    const latestImages = jobs[0]?.imageUrls || [];
    const imageUrls = timelineCoverImage
      ? [timelineCoverImage, ...latestImages.filter(url => url !== timelineCoverImage)]
      : latestImages;

    return {
      current: {
        clientName: timeline.clientName || '',
        clientAddress: timeline.clientAddress || '',
        chimneyType: 'masonry',
        reportDate: new Date().toISOString().split('T')[0],
        timelineContent: '',
        imageUrls,
        timelineCoverImage,
        scrapedImages: imageUrls.map((url, index) => ({
          id: `scraped-${index}-${Date.now()}`,
          url,
          alt: `Scraped image ${index + 1}`
        }))
      },
      jobs
    };
  };

  // Function to find the previous job by date
  const findPreviousJobByDate = (jobs: JobData[]): JobData | null => {
    if (jobs.length < 2) {
      return null; // Need at least 2 jobs to find a previous one
    }
    
    // Jobs are already sorted by date (newest first)
    // Return the second job (previous to the latest)
    return jobs[1];
  };

  const handleScrape = async () => {
    if (!url) return;
    
    const reportId = extractedData.clientName || 'extraction_' + Date.now();
    
    // Log extraction started
    await logger.logExtractionStarted(userEmail, reportId, {
      source: 'capturify',
      url: url
    });
    
    setIsLoading(true);
    try {
      const token = extractCapturifyToken(url);
      if (!token) {
        throw new Error('Not a Capturify timeline link');
      }

      // Goes through our own server (server.js) - the Capturify API doesn't
      // allow CORS from this app's origin.
      const response = await fetch(`/api/capturify/timeline/${encodeURIComponent(token)}`);
      const timeline = await response.json().catch(() => null);
      if (!response.ok || !timeline) {
        throw new Error(timeline?.message || timeline?.detail || `HTTP error! status: ${response.status}`);
      }
      
      const { current: parsedData, jobs: allJobsData } = parseCapturifyTimeline(timeline);
      setAllJobs(allJobsData);
      
      setExtractedData(parsedData);
      setShowEditForm(true);
      
      // Log extraction success
      await logger.logExtractionSuccess(userEmail, reportId, {
        client_name: parsedData.clientName,
        images_count: parsedData.scrapedImages?.length || 0,
        has_address: !!parsedData.clientAddress
      });
      
    } catch (error: any) {
      console.error('Data extraction failed:', error);
      
      // Log extraction failure
      await logger.logExtractionFailed(userEmail, reportId, error?.message || 'Unknown error', {
        url: url,
        error_type: error?.name
      });
      
      alert('Data extraction failed. Please verify the URL and try again, or contact IT support if the issue persists.');
    } finally {
      setIsLoading(false);
    }
  };


  const handleDataChange = (field: keyof ExtractedData, value: string) => {
    setExtractedData(prev => ({ ...prev, [field]: value }));
  };

  const handleDateSelection = (job: JobData) => {
    setSelectedDateJob(job);
    setSelectedImages([]); // Reset selected images when selecting a new date
    setShowDateSelector(false);
    setShowPreviousJobImages(true);
  };

  const handleImageSelection = (imageUrl: string) => {
    setSelectedImages(prev => {
      if (prev.includes(imageUrl)) {
        // Remove image if already selected
        return prev.filter(url => url !== imageUrl);
      } else {
        // Add image if not selected
        return [...prev, imageUrl];
      }
    });
  };

  const handleSelectAllImages = () => {
    if (!selectedDateJob) return;
    
    if (selectedImages.length === selectedDateJob.imageUrls.length) {
      // If all images are selected, deselect all
      setSelectedImages([]);
    } else {
      // Select all images
      setSelectedImages([...selectedDateJob.imageUrls]);
    }
  };

  const handleMergeSelectedDateImages = () => {
    if (!selectedDateJob || selectedImages.length === 0) return;
    
    // Convert selected image URLs to ImageItem format
    const selectedImageItems: ImageItem[] = selectedImages.map((url, index) => ({
      id: `selected-${index}-${Date.now()}`,
      url: url,
      alt: `Selected image from ${selectedDateJob.date}`
    }));
    
    // Merge only selected images with current job images
    const mergedImages = [...extractedData.scrapedImages, ...selectedImageItems];
    const mergedImageUrls = [...extractedData.imageUrls, ...selectedImages];
    
    setExtractedData(prev => ({
      ...prev,
      scrapedImages: mergedImages,
      imageUrls: mergedImageUrls
    }));
    
    setShowPreviousJobImages(false);
    setSelectedDateJob(null);
    setSelectedImages([]);
  };

  const handleContinue = () => {
    // Only pass the required data to the parent component
    // Report creation and logging will happen in MultiStepForm
    onDataExtracted({
      clientName: extractedData.clientName,
      clientAddress: extractedData.clientAddress,
      chimneyType: extractedData.chimneyType,
      reportDate: extractedData.reportDate,
      timelineCoverImage: extractedData.timelineCoverImage || '', // Use specific timeline cover image
      scrapedImages: extractedData.scrapedImages || [],
      dataSourceUrl: url // Pass the data source URL
    });
  };

  return (
    <div className="space-y-4 sm:space-y-5 p-4 sm:p-6">
      {/* URL Input */}
      <div className="bg-white border border-gray-200 p-4 sm:p-6 rounded-xl shadow-sm">
        <form onSubmit={(e) => { e.preventDefault(); if (url && !isLoading) handleScrape(); }} className="space-y-4">
          <div>
            <label htmlFor="dataSource" className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2">
              <Icon.Link className="w-4 h-4 text-[#722420]" />
              Data Source URL <span className="text-[#722420]">*</span>
            </label>
            <div className="relative">
              <Icon.Link className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="url"
                id="dataSource"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://capturify.chimneysweeps.com/timeline/..."
                className="w-full pl-10 pr-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#722420]/20 focus:border-[#722420]"
                required
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              type="submit"
              disabled={!url || isLoading}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 bg-[#722420] text-white rounded-lg hover:bg-[#8B2E26] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              <Icon.Zap className="w-4 h-4" /> {isLoading ? 'Extracting…' : 'Extract Data'}
            </button>
            <button
              type="button"
              onClick={() => setShowDateSelector(true)}
              disabled={!url || allJobs.length === 0}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              title="Select images from any available date"
            >
              <Icon.Calendar className="w-4 h-4" /> Select Date Images
            </button>
            <button
              type="button"
              onClick={() => {
                setFormData({
                  clientName: '',
                  clientAddress: '',
                  chimneyType: '',
                  reportDate: new Date().toISOString().split('T')[0],
                  timelineCoverImage: '',
                  scrapedImages: []
                });
                setCurrentStep('form');
              }}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
            >
              <Icon.Edit className="w-4 h-4" /> Enter Manually
            </button>
          </div>
        </form>
      </div>

      {/* Extracted Data Display/Edit */}
      {showEditForm && (
        <div className="bg-green-50 border border-green-200 p-4 sm:p-6 rounded-lg">
          <h3 className="text-base sm:text-lg font-semibold text-green-900 mb-3 sm:mb-4">Extracted Information</h3>
          <p className="text-sm sm:text-base text-green-700 mb-3 sm:mb-4">Review and verify the extracted data. Make any necessary corrections before proceeding.</p>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left side - House Image */}
              <div className="flex flex-col items-center lg:col-span-1">
                <h5 className="font-medium text-gray-700 mb-3">House Image:</h5>
                {extractedData.timelineCoverImage ? (
                  <>
                    <img 
                      src={extractedData.timelineCoverImage} 
                      alt="Timeline Cover" 
                      className="w-48 h-50 object-cover rounded-lg border-2 border-gray-200 shadow-md"
                    />
                    {extractedData.imageUrls.length > 1 && (
                      <button
                        onClick={() => setShowImageSelector(true)}
                        className="mt-3 px-4 py-2 bg-[#722420] text-white rounded-lg hover:bg-[#5a1d1a] transition-colors text-sm"
                      >
                        Change the house image
                      </button>
                    )}
                  </>
                ) : (
                  <div className="w-48 h-50 bg-gray-100 border-2 border-gray-200 rounded-lg flex items-center justify-center">
                    <p className="text-gray-500 text-sm text-center">No image available</p>
                  </div>
                )}
                
                {/* No House Image Button */}
                <button
                  onClick={() => setShowNoImagePopup(true)}
                  className="mt-3 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                >
                  No House Image
                </button>
              </div>
              
              {/* Right side - Form Fields */}
              <div className="space-y-6 lg:col-span-2">
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Client Name</p>
                  <h4 className="text-xl font-bold text-gray-900">
                    {extractedData.clientName || 'Client Name Not Extracted'}
                  </h4>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Client Address</p>
                  <h4 className="text-xl font-bold text-gray-900">
                    {extractedData.clientAddress || 'Address Not Extracted'}
                  </h4>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <label htmlFor="extractedChimneyType" className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                    Chimney Type
                  </label>
                  <div className="relative">
                    <select
                      id="extractedChimneyType"
                      value={extractedData.chimneyType}
                      onChange={(e) => handleDataChange('chimneyType', e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#722420] focus:border-[#722420] text-gray-900 font-medium transition-all duration-200 appearance-none cursor-pointer hover:border-gray-300 hover:bg-gray-100"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 12px center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '16px',
                        paddingRight: '40px'
                      }}
                    >
                      <option value="" className="text-gray-500">Select chimney type</option>
                      <option value="masonry" className="text-gray-900 font-medium">Masonry</option>
                      <option value="prefabricated" className="text-gray-900 font-medium">Prefabricated</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  {extractedData.chimneyType && (
                    <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-xs text-green-700 font-medium">
                        ✓ Selected: <span className="capitalize">{extractedData.chimneyType}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          
          <div className="mt-4 sm:mt-6">
            <button
              onClick={handleContinue}
              className="w-full px-4 sm:px-6 py-3 bg-[#722420] text-white rounded-md hover:bg-[#5a1d1a] font-medium"
            >
              Proceed to Report Generation
            </button>
          </div>
        </div>
      )}

      {/* Company Guidelines */}
      <div className="relative bg-[#FBEEED] border border-[#722420]/15 p-4 sm:p-5 rounded-xl overflow-hidden">
        <Icon.Shield className="w-5 h-5 text-[#722420]/40 absolute top-4 right-4" />
        <div className="flex items-center gap-2 mb-4">
          <Icon.AlertCircle className="w-4 h-4 text-[#722420]" />
          <h4 className="text-sm font-semibold text-[#722420]">Company Guidelines</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            'Use this tool to extract data from company-approved sources only',
            'Always verify extracted information before generating reports',
            'Ensure client data accuracy for compliance and quality standards',
            'Contact your supervisor if you encounter data extraction issues',
          ].map((text) => (
            <div key={text} className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-white text-[#722420] flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><path d="M20 6L9 17l-5-5" /></svg>
              </div>
              <p className="text-xs sm:text-sm text-[#722420]/85">{text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Image Selection Modal */}
      {showImageSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Select House Image</h3>
                <button
                  onClick={() => setShowImageSelector(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">Choose from the available images</p>
            </div>
            
            <div className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {extractedData.imageUrls.map((imageUrl, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={imageUrl}
                      alt={`House Image ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 cursor-pointer hover:border-[#722420] transition-colors"
                      onClick={() => {
                        handleDataChange('timelineCoverImage', imageUrl);
                        setShowImageSelector(false);
                      }}
                    />
                    {extractedData.timelineCoverImage === imageUrl && (
                      <div className="absolute top-2 right-2 bg-[#722420] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
                        ✓
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                      Click to select
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No House Image Popup */}
      {showNoImagePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                  <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No House Image</h3>
                <p className="text-sm text-gray-600">Would you like to proceed without a house image or select one from the available images?</p>
              </div>
              
              <div className="flex flex-col space-y-3">
                <button
                  onClick={() => {
                    handleDataChange('timelineCoverImage', '');
                    setShowNoImagePopup(false);
                  }}
                  className="w-full px-4 py-2 bg-[#722420] text-white rounded-lg hover:bg-[#5a1d1a] transition-colors font-medium"
                >
                  Proceed Without Image
                </button>
                
                {extractedData.imageUrls.length > 0 && (
                  <button
                    onClick={() => {
                      setShowNoImagePopup(false);
                      setShowImageSelector(true);
                    }}
                    className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  >
                    Select from Available Images
                  </button>
                )}
                
                <button
                  onClick={() => setShowNoImagePopup(false)}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Selector Modal */}
      {showDateSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Select Date for Images</h3>
                <button
                  onClick={() => setShowDateSelector(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Choose a date to view and merge its images with your current job.
              </p>
            </div>
            
            <div className="p-4">
              {allJobs.length > 0 ? (
                <div className="space-y-3">
                  {allJobs.map((job, index) => (
                    <div 
                      key={job.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer"
                      onClick={() => handleDateSelection(job)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {job.date}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {job.imageUrls.length} image{job.imageUrls.length !== 1 ? 's' : ''} available
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">
                            {index === 0 ? 'Latest' : index === 1 ? 'Previous' : `${index + 1}${index === 2 ? 'rd' : index === 3 ? 'th' : 'th'}`}
                          </span>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                    <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Dates Found</h3>
                  <p className="text-sm text-gray-600 mb-4">No jobs with dates were found in the HTML content.</p>
                  <button
                    onClick={() => setShowDateSelector(false)}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Selected Date Images Modal */}
      {showPreviousJobImages && selectedDateJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Images from {selectedDateJob.date}</h3>
                <button
                  onClick={() => {
                    setShowPreviousJobImages(false);
                    setSelectedDateJob(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Found {selectedDateJob.imageUrls.length} image{selectedDateJob.imageUrls.length !== 1 ? 's' : ''} from {selectedDateJob.date}. 
                Select the images you want to add to your current job.
              </p>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleSelectAllImages}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {selectedImages.length === selectedDateJob.imageUrls.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className="text-sm text-gray-500">
                    {selectedImages.length} of {selectedDateJob.imageUrls.length} selected
                  </span>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              {selectedDateJob.imageUrls.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                    {selectedDateJob.imageUrls.map((imageUrl, index) => {
                      const isSelected = selectedImages.includes(imageUrl);
                      return (
                        <div 
                          key={index} 
                          className={`relative group cursor-pointer ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                          onClick={() => handleImageSelection(imageUrl)}
                        >
                          <img
                            src={imageUrl}
                            alt={`Image from ${selectedDateJob.date} ${index + 1}`}
                            className={`w-full h-32 object-cover rounded-lg border-2 transition-all ${
                              isSelected ? 'border-blue-500' : 'border-gray-200'
                            }`}
                          />
                          
                          {/* Selection checkbox */}
                          <div className="absolute top-2 right-2">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              isSelected 
                                ? 'bg-blue-500 border-blue-500' 
                                : 'bg-white border-gray-300'
                            }`}>
                              {isSelected && (
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                          
                          {/* Date overlay */}
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                            {selectedDateJob.date}
                          </div>
                          
                          {/* Selection indicator */}
                          {isSelected && (
                            <div className="absolute inset-0 bg-blue-500 bg-opacity-20 rounded-lg"></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={handleMergeSelectedDateImages}
                      disabled={selectedImages.length === 0}
                      className={`px-6 py-2 rounded-lg transition-colors font-medium ${
                        selectedImages.length === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Merge Selected Images ({selectedImages.length})
                    </button>
                    <button
                      onClick={() => {
                        setShowPreviousJobImages(false);
                        setSelectedDateJob(null);
                        setSelectedImages([]);
                      }}
                      className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                    <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Images Found</h3>
                  <p className="text-sm text-gray-600 mb-4">No images were found for {selectedDateJob.date}.</p>
                  <button
                    onClick={() => {
                      setShowPreviousJobImages(false);
                      setSelectedDateJob(null);
                    }}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Library Modal */}
      {showLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Library</h3>
              <button
                onClick={() => setShowLibrary(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-6">
              {libraryError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded">{libraryError}</div>
              )}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Top-level directories</h4>
                {libraryLoading && libraryRootDirs.length === 0 ? (
                  <p className="text-sm text-gray-500">Loading…</p>
                ) : libraryRootDirs.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {libraryRootDirs.map((d) => (
                      <button
                        key={d.prefix}
                        onClick={() => loadChildDirectories(d.prefix)}
                        className="w-full text-left p-3 border border-gray-200 rounded hover:border-blue-300 hover:bg-blue-50"
                      >
                        <div className="font-medium text-gray-900">{d.name}</div>
                        <div className="text-xs text-gray-500">{d.prefix}</div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No directories found.</p>
                )}
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Subdirectories</h4>
                {libraryLoading && libraryChildDirs.length === 0 ? (
                  <p className="text-sm text-gray-500">Loading…</p>
                ) : libraryChildDirs.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {libraryChildDirs.map((d) => (
                      <div key={d.prefix} className="p-3 border border-gray-200 rounded">
                        <div className="font-medium text-gray-900">{d.name}</div>
                        <div className="text-xs text-gray-500">{d.prefix}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Select a directory to view subdirectories.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataScraper;
